import {error} from '.';
import {emapServerBase} from '../config/environment';
import incident_types from '../config/incident_types';
import {INCIDENT_STATUSES, INCIDENT_TYPE_KEYS} from '../Constants';
import {fetchJson} from '../utils/fetch_utils';
import {formatDefault} from '../utils/time_utils';
import * as ActionTypes from './actionTypes';

const EXCLUDED_BURN_TYPES = ['SLASHING / MOWING',
                             'MULCHING',
                             'SPRAYING',
                             'ROLLING',
                             'PLOUGHING',
                             'STRATEGIC FUEL BREAK - LANDSCAPE'];

export const populateBushfiresDataAsync = (poller) => {
  let bushfireIncidentType = incident_types.find(incident_type => INCIDENT_TYPE_KEYS.BUSHFIRES === incident_type.id);
  if (bushfireIncidentType) {
    return (dispatch) => {
      fetchJson(encodeURI(`${emapServerBase}${bushfireIncidentType.source}`))
      .then(data => { dispatch(_populateBushfireDetails(data.features, poller)); })
      .catch(e => { dispatch(error(`Call to retrieve Bushfire data failed: ${e.message || e}`)); });
    };
  }
};

export const populateBurnsDataAsync = (incident_type_key, poller) => {
  const incidentType = incident_types.find(it => incident_type_key === it.id);
  if (incidentType) {
    return (dispatch) => {
      fetchJson(encodeURI(`${emapServerBase}${incidentType.source}`))
      .then(data => {
        data.features =
          data.features.filter(f => _burnsFilter(f, incidentType.status, incidentType.additionalStatuses));
        if (incidentType.burnAgency) {
          data.features = data.features.filter(
            f => burnAgencyFromEmapFeature(f) === incidentType.burnAgency
          );
        }
        return data;
      })
      .then(_setIncidentColor(incidentType))
      .then(data => {
        dispatch(_populateBurnDetails(incidentType, data.features, poller));
      })
      .catch(e => {dispatch(error(`Call to retrieve Incident data failed: ${e.message || e}`));});
    };
  }
};

export const populateCurrentBurntAreaAsync = () => {
  const incidentType = incident_types.find(
    incident_type => INCIDENT_TYPE_KEYS.CURRENT_BURNT_AREA === incident_type.id
  );
  if (incidentType) {
    return (dispatch) => {
      fetchJson(encodeURI(`${emapServerBase}${incidentType.source}`))
      .then(data => {
        // Filter the properties down to just the Incident Name
        data.features = data.features
                            .map(feature => ({
                              ...feature,
                              properties: ({
                                Name: (feature.properties || {}).INCIDENT_NAME || ''
                              })
                            }));
        dispatch({
          type: ActionTypes.POPULATE_BURNT_AREA_POLYGONS,
          burntArea: data,
          updated_at: new Date()
        });
      })
      .catch(e => {
        dispatch(error(`Call to retrieve Current Burnt Area data failed: ${e.message || e}`));
      });
    };
  }
};

export const populateBurnPolgonsAsync = (metadata) => {
  return (dispatch) => {
    fetchJson(encodeURI(`${emapServerBase}${metadata.source}`))
    .then(data => {
      data.features = data.features.filter(f => _burnsFilter(f));
      return data;
    })
    .then(_populateIncidentProperties)
    .then(data => {
      dispatch({
        type: ActionTypes.POPULATE_BURN_POLYGONS,
        polygons: data,
        updated_at: new Date()
      });
    })
    .catch(e => {dispatch(error(`Call to retrieve Burn Polygon data failed: ${e.message || e}`));});
  };
};

const _setIncidentColor = (metadata) => (data) => {
  data.features.forEach(f => {
    const burnStatus = _burnPropertiesFromEmapFeature(f).BURN_STATUS;
    if (burnStatus === metadata.status) {
      f.polyColor = metadata.polyColor;
    } else if (metadata.additionalStatuses) {
      const idx = metadata.additionalStatuses.indexOf(burnStatus);
      if (idx >= 0) {
        f.polyColor = metadata.additionalPolyColors[idx];
      }
    }
  });
  return data;
};

const _populateIncidentProperties = (data) => {
  data.features.forEach(f => {
    const p = f.properties;
    delete f.properties;
    f.properties = {
      Name: p.NAME,
      Number: p.TREATMENT_NO,
      Status: p.BURN_STATUS,
      Area: _formatArea(p.AREA)
    };
    f.eventId = p.EVENTID;
  });
  return data;
};

const _formatArea = (area) => {
  if (area) {
    if (area > 100) {
      return area.toFixed(0);
    }
    return area.toFixed(1);
  }
  return '';
};

const _populateBurnDetails = (incidentType, burns, poller) => {
  const geoJSON = {
    type: 'FeatureCollection',
    features: burns.map(feature => {
      const attributes = _burnPropertiesFromEmapFeature(feature);
      const coordinates = _burnPointCoordinatesFromEmapFeature(feature);
      const properties = {
        Name: attributes.BURN_NAME,
        Number: attributes.BURN_NUMBER,
        Type: attributes.BURN_TYPE,
        Status: attributes.BURN_STATUS,
        Location: attributes.LOCATION
      };
      return {
        type: 'Feature',
        geometry: { type: 'Point', coordinates },
        properties,
        icon: attributes.BURN_STATUS.toLowerCase().replace(/ /g, '_'),
        eventId: attributes.EVENTID,
        polyColor: feature.polyColor
      };
    })
  };
  return {
    type: ActionTypes.POPULATE_BURN_DETAILS,
    burns: geoJSON,
    incidentTypeKey: incidentType.id,
    updated_at: new Date(),
    poller
  };
};

// Exclude burns that are nopolygonst really burns.
const _burnsFilter = (feature, status, additionalStatuses) => {
  // The feature will either have properties or attributes depending on if it is geojson or just json.
  const properties = feature.properties || feature.attributes;
  if (status && status !== properties.BURN_STATUS) {
    if (additionalStatuses) {
      if (!additionalStatuses.includes(properties.BURN_STATUS)) {
        return false;
      }
    } else {
      return false;
    }
  }
  // if (properties.TREATMENT_NO && properties.TREATMENT_NO.match(/^D\d\d-.*/)) {
  //   return false;
  // }
  // if (properties.BURN_NUMBER && properties.BURN_NUMBER.match(/^D\d\d-.*/)) {
  //   return false;
  // }
  return !EXCLUDED_BURN_TYPES.includes(properties.BURN_TYPE);
};

const _populateBushfireDetails = (bushfires, poller) => {
  const geoJSON = {
    type: 'FeatureCollection',
    features: bushfires
    .filter(feature => INCIDENT_STATUSES.includes(feature.attributes.Status.toUpperCase()))
    .map(feature => {
      let attributes = feature.attributes;
      let properties = {
        Name: attributes.NAME,
        Agency: attributes.Agency.toUpperCase() === 'DEPI' ? 'DELWP' : attributes.Agency,
        Status: attributes.Status,
        Location: attributes.Location,
        Start_date: formatDefault(_adjustDateToUTC(attributes.INC_DATE_TIME)),
        Area: attributes.Size
      };
      let icon;
      if (-1 !== attributes.Status.toLowerCase().indexOf('under control')) {
        icon = 'controlled';
      } else {
        icon = attributes.Status.toLowerCase().replace(/ /g, '_');
      }
      return {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [feature.geometry.x, feature.geometry.y] },
        properties: properties,
        icon: icon
      };
    })
  };
  return { type: ActionTypes.POPULATE_BUSHFIRE_DETAILS, bushfires: geoJSON, updated_at: new Date(), poller };
};

const _adjustDateToUTC = (time) => {
  return time + (new Date(time).getTimezoneOffset() * 60 * 1000);
};

const _burnPropertiesFromEmapFeature = (feature) => {
  return feature.attributes || feature.properties || {};
};

const _burnPointCoordinatesFromEmapFeature = (feature) => {
  if (feature.geometry && feature.geometry.coordinates) {
    return feature.geometry.coordinates;
  }
  if (feature.geometry) {
    return [feature.geometry.x, feature.geometry.y];
  }
  return [undefined, undefined];
};

/** Exported for testing */
export const burnAgencyFromEmapFeature = (feature) => {
  const properties = _burnPropertiesFromEmapFeature(feature);
  const num = String(properties.BURN_NUMBER || properties.TREATMENT_NO || '').trim();
  if (/^D\d{2}-\d+-\d+$/i.test(num)) {
    return 'CFA';
  }
  return 'FFMVIC';
};



// WEBPACK FOOTER //
// ./src/actions/populate_incident_data.js